import type {
  UpdateWorkplaceRequestParams,
  WorkplaceRequestBatchUpdateResult,
  WorkplaceRequestElement
} from "@gettactic/api";

/** `PUT /tickets` accepts at most this many tickets per call. */
export const MAX_REQUESTS_PER_BATCH = 100;

export interface WorkplaceRequestUpdateEntry {
  requestId: string;
  updates: UpdateWorkplaceRequestParams;
  /** Shown in the failure toast so the user knows which request failed. */
  title?: string;
}

export interface WorkplaceRequestUpdateFailure {
  requestId: string;
  title?: string;
  error: { code: string; message: string };
}

export interface WorkplaceRequestsBatchOutcome {
  successful: Array<{ requestId: string; result: WorkplaceRequestElement }>;
  failed: WorkplaceRequestUpdateFailure[];
  totalRequested: number;
  totalSuccessful: number;
  totalFailed: number;
}

/**
 * Group the entries into bodies for `PUT /tickets`: keyed by ticket id and no
 * larger than the endpoint allows. A request listed twice keeps its last
 * update, since the endpoint rejects duplicate ids for the whole call.
 */
export const toBatchBodies = (
  entries: WorkplaceRequestUpdateEntry[],
  maxPerBatch = MAX_REQUESTS_PER_BATCH
): Array<Record<string, UpdateWorkplaceRequestParams>> => {
  const byId = new Map<string, UpdateWorkplaceRequestParams>();
  for (const entry of entries) {
    byId.set(entry.requestId, entry.updates);
  }

  const bodies: Array<Record<string, UpdateWorkplaceRequestParams>> = [];
  let current: Record<string, UpdateWorkplaceRequestParams> = {};
  let currentSize = 0;
  for (const [requestId, updates] of byId) {
    if (currentSize === maxPerBatch) {
      bodies.push(current);
      current = {};
      currentSize = 0;
    }
    current[requestId] = updates;
    currentSize += 1;
  }
  if (currentSize > 0) bodies.push(current);
  return bodies;
};

const MISSING_RESULT_ERROR = {
  code: "missing_result",
  message: "No result was returned for this request"
};

/**
 * Fold the per-ticket results of one or more batch calls into the counts the
 * toast reads. A request the server didn't answer for counts as failed, so a
 * partial response can't read as a full success.
 */
export const summarizeBatchResults = (
  entries: WorkplaceRequestUpdateEntry[],
  results: WorkplaceRequestBatchUpdateResult[]
): WorkplaceRequestsBatchOutcome => {
  const resultsById = new Map(
    results.map((result) => [result.ticket_id, result] as const)
  );
  const titlesById = new Map(
    entries.map((entry) => [entry.requestId, entry.title] as const)
  );
  const requestIds = [...new Set(entries.map((entry) => entry.requestId))];

  const successful: WorkplaceRequestsBatchOutcome["successful"] = [];
  const failed: WorkplaceRequestUpdateFailure[] = [];
  for (const requestId of requestIds) {
    const result = resultsById.get(requestId);
    const title = titlesById.get(requestId);
    if (result?.ticket && !result.error) {
      successful.push({ requestId, result: result.ticket });
    } else {
      failed.push({
        requestId,
        title,
        error: result?.error ?? MISSING_RESULT_ERROR
      });
    }
  }

  return {
    successful,
    failed,
    totalRequested: requestIds.length,
    totalSuccessful: successful.length,
    totalFailed: failed.length
  };
};

/**
 * The per-ticket results for a batch whose whole call failed (HTTP or network
 * error), so its requests count as failed alongside the others instead of
 * aborting the run.
 */
export const toFailedBatchResults = (
  body: Record<string, UpdateWorkplaceRequestParams>,
  message: string
): WorkplaceRequestBatchUpdateResult[] =>
  Object.keys(body).map((ticketId) => ({
    ticket_id: ticketId,
    error: { code: "request_failed", message }
  }));

const MAX_FAILURES_LISTED = 3;

/**
 * One line per failed request for the toast, e.g. "Printer broken: invalid
 * owner user id", capped so a big selection doesn't flood the screen.
 */
export const describeBatchFailures = (
  failed: WorkplaceRequestUpdateFailure[]
): string[] => {
  const lines = failed
    .slice(0, MAX_FAILURES_LISTED)
    .map((failure) =>
      failure.title
        ? `${failure.title}: ${failure.error.message}`
        : failure.error.message
    );
  const remaining = failed.length - lines.length;
  if (remaining > 0) {
    lines.push(`and ${remaining} more`);
  }
  return lines;
};
